import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Transcript, Speaker, TranscriptSegment, FilterState, ViewMode } from '@/types';

interface TranscriptStore {
  // Data
  transcript: Transcript | null;
  
  // UI State
  filters: FilterState;
  viewMode: ViewMode;
  selectedSegmentId: string | null;
  isEditing: boolean;
  showAbsoluteTime: boolean;
  
  // Actions
  setTranscript: (transcript: Transcript) => void;
  updateSegment: (segmentId: string, updates: Partial<TranscriptSegment>) => void;
  updateSpeaker: (speakerId: string, updates: Partial<Speaker>) => void;
  addSpeaker: (speaker: Speaker) => void;
  removeSpeaker: (speakerId: string) => void;
  mergeSpeakers: (sourceId: string, targetId: string) => void;
  
  // Filter actions
  setSelectedSpeakers: (speakerIds: string[]) => void;
  toggleSpeaker: (speakerId: string) => void;
  setSearchQuery: (query: string) => void;
  setTimeRange: (range: { start: number; end: number } | null) => void;
  clearFilters: () => void;
  
  // UI actions
  setViewMode: (mode: ViewMode) => void;
  setSelectedSegment: (segmentId: string | null) => void;
  setIsEditing: (isEditing: boolean) => void;
  toggleAbsoluteTime: () => void;
  
  // Computed
  getFilteredSegments: () => TranscriptSegment[];
}

const initialFilters: FilterState = {
  selectedSpeakers: [],
  searchQuery: '',
  timeRange: null,
};

export const useTranscriptStore = create<TranscriptStore>()(
  persist(
    (set, get) => ({
      // Initial state
      transcript: null,
      filters: initialFilters,
      viewMode: 'timeline',
      selectedSegmentId: null,
      isEditing: false,
      showAbsoluteTime: false,

      // Data actions
      setTranscript: (transcript) => set({ transcript, filters: initialFilters }),

      updateSegment: (segmentId, updates) =>
        set((state) => {
          if (!state.transcript) return state;
          return {
            transcript: {
              ...state.transcript,
              segments: state.transcript.segments.map((seg) =>
                seg.id === segmentId ? { ...seg, ...updates, edited: true } : seg
              ),
            },
          };
        }),

      updateSpeaker: (speakerId, updates) =>
        set((state) => {
          if (!state.transcript) return state;
          return {
            transcript: {
              ...state.transcript,
              speakers: state.transcript.speakers.map((speaker) =>
                speaker.id === speakerId ? { ...speaker, ...updates } : speaker
              ),
            },
          };
        }),

      addSpeaker: (speaker) =>
        set((state) => {
          if (!state.transcript) return state;
          return {
            transcript: {
              ...state.transcript,
              speakers: [...state.transcript.speakers, speaker],
            },
          };
        }),

      removeSpeaker: (speakerId) =>
        set((state) => {
          if (!state.transcript) return state;
          return {
            transcript: {
              ...state.transcript,
              speakers: state.transcript.speakers.filter((s) => s.id !== speakerId),
            },
          };
        }),

      mergeSpeakers: (sourceId, targetId) =>
        set((state) => {
          if (!state.transcript) return state;
          return {
            transcript: {
              ...state.transcript,
              segments: state.transcript.segments.map((seg) =>
                seg.speakerId === sourceId ? { ...seg, speakerId: targetId } : seg
              ),
              speakers: state.transcript.speakers.filter((s) => s.id !== sourceId),
            },
          };
        }),

      // Filter actions
      setSelectedSpeakers: (speakerIds) =>
        set((state) => ({
          filters: { ...state.filters, selectedSpeakers: speakerIds },
        })),

      toggleSpeaker: (speakerId) =>
        set((state) => {
          const current = state.filters.selectedSpeakers;
          const newSpeakers = current.includes(speakerId)
            ? current.filter((id) => id !== speakerId)
            : [...current, speakerId];
          return {
            filters: { ...state.filters, selectedSpeakers: newSpeakers },
          };
        }),

      setSearchQuery: (query) =>
        set((state) => ({
          filters: { ...state.filters, searchQuery: query },
        })),

      setTimeRange: (range) =>
        set((state) => ({
          filters: { ...state.filters, timeRange: range },
        })),

      clearFilters: () =>
        set({ filters: initialFilters }),

      // UI actions
      setViewMode: (mode) => set({ viewMode: mode }),
      setSelectedSegment: (segmentId) => set({ selectedSegmentId: segmentId }),
      setIsEditing: (isEditing) => set({ isEditing }),
      toggleAbsoluteTime: () => set((state) => ({ showAbsoluteTime: !state.showAbsoluteTime })),

      // Computed
      getFilteredSegments: () => {
        const { transcript, filters } = get();
        if (!transcript) return [];

        let segments = [...transcript.segments];

        // Filter by speakers
        if (filters.selectedSpeakers.length > 0) {
          segments = segments.filter((seg) =>
            filters.selectedSpeakers.includes(seg.speakerId)
          );
        }

        // Filter by search query
        if (filters.searchQuery.trim()) {
          const query = filters.searchQuery.toLowerCase();
          segments = segments.filter((seg) =>
            seg.text.toLowerCase().includes(query)
          );
        }

        // Filter by time range
        if (filters.timeRange) {
          segments = segments.filter(
            (seg) =>
              seg.startTime >= filters.timeRange!.start &&
              seg.endTime <= filters.timeRange!.end
          );
        }

        return segments;
      },
    }),
    {
      name: 'transcript-storage',
      partialize: (state) => ({
        transcript: state.transcript,
        viewMode: state.viewMode,
      }),
    }
  )
);
